import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { DynamicIcon } from "@/lib/icons";

export function SmartSearch({ variant = "desktop", onNavigate }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ products: [], categories: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { currency } = useStore();
  const boxRef = useRef(null);

  useEffect(() => {
    const handler = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults({ products: [], categories: [] });
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/suggest", { params: { q: query.trim() } });
        setResults(data);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [query]);

  const go = (path) => {
    setOpen(false);
    setQuery("");
    onNavigate?.();
    navigate(path);
  };

  const submit = (event) => {
    event.preventDefault();
    if (!query.trim()) return;
    go(`/boutique?q=${encodeURIComponent(query.trim())}`);
  };

  const isMobile = variant === "mobile";

  return (
    <div ref={boxRef} className="relative w-full">
      <form onSubmit={submit} className="relative flex w-full items-center">
        <button
          type="button"
          aria-label="Recherche par image"
          title="Rechercher par image"
          data-testid={isMobile ? "mobile-visual-search-btn" : "visual-search-btn"}
          onClick={() => go("/recherche-visuelle")}
          className={`absolute left-1 flex items-center justify-center rounded-full text-slate-400 transition-colors duration-200 hover:bg-brand-soft hover:text-brand ${
            isMobile ? "h-9 w-9" : "h-10 w-10"
          }`}
        >
          <Camera className="h-[18px] w-[18px]" strokeWidth={2.2} />
        </button>
        <input
          data-testid={isMobile ? "mobile-search-input" : "desktop-search-input"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim().length >= 2 && setOpen(true)}
          placeholder={isMobile ? "Rechercher un produit..." : "Rechercher une licence, un logiciel..."}
          className={`w-full rounded-full border border-line bg-slate-50 pl-[52px] pr-[52px] font-medium text-ink placeholder:text-slate-400 focus:border-brand focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand/10 ${
            isMobile ? "h-11 text-sm" : "h-12 text-[15px]"
          } transition-colors duration-200`}
        />
        {query && (
          <button
            type="button"
            aria-label="Effacer"
            data-testid="search-clear-btn"
            onClick={() => setQuery("")}
            className="absolute right-[54px] text-slate-400 transition-colors duration-200 hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <button
          type="submit"
          data-testid={isMobile ? "mobile-search-submit" : "desktop-search-submit"}
          className={`absolute right-1 flex items-center justify-center rounded-full bg-brand text-white transition-colors duration-200 hover:bg-brand-dark ${
            isMobile ? "h-9 w-9" : "h-10 w-11"
          }`}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" strokeWidth={2.6} />}
        </button>
      </form>

      {open && (results.products.length > 0 || results.categories.length > 0) && (
        <div
          data-testid="search-suggestions"
          className="absolute left-0 right-0 top-[calc(100%+10px)] z-[60] overflow-hidden rounded-2xl border border-line bg-white shadow-[0_24px_60px_-15px_rgba(0,0,0,0.25)]"
        >
          {results.categories.length > 0 && (
            <div className="border-b border-line bg-slate-50/60 px-4 py-3">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Catégories</p>
              <div className="flex flex-wrap gap-2">
                {results.categories.map((cat) => (
                  <button
                    key={cat.slug}
                    data-testid={`suggest-category-${cat.slug}`}
                    onClick={() => go(`/boutique?categorie=${cat.slug}`)}
                    className="flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
                  >
                    <DynamicIcon name={cat.icon} className="h-3.5 w-3.5" />
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <ul className="max-h-[340px] overflow-y-auto">
            {results.products.map((product) => (
              <li key={product.id}>
                <button
                  data-testid={`suggest-product-${product.slug}`}
                  onClick={() => go(`/boutique/${product.slug}`)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-200 hover:bg-brand-soft"
                >
                  <img
                    src={product.image_url}
                    alt={product.name}
                    loading="lazy"
                    className="h-11 w-11 rounded-lg border border-line bg-white object-contain p-1"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">{product.name}</span>
                    <span className="block text-xs text-slate-500">{product.license_type}</span>
                  </span>
                  <span className="whitespace-nowrap text-sm font-bold text-brand">
                    {formatPrice(product.price, currency)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button
            onClick={() => go(`/boutique?q=${encodeURIComponent(query.trim())}`)}
            data-testid="search-see-all"
            className="w-full border-t border-line bg-slate-50 px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-brand transition-colors duration-200 hover:bg-brand-light"
          >
            Voir tous les résultats
          </button>
        </div>
      )}
    </div>
  );
}

export default SmartSearch;
